
// HeaderTab.jsx - Header tab component
import React from 'react';
import {
  Box,
  Grid,
  TextInput,
  Select,
  Group,
  Text,
  Paper,
  Stack,
  Button,
  NumberInput,
  Badge
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';

const HeaderTab = ({ quoteData, setQuoteData }) => {
  return (
    <Box p="md">
      <Grid gutter="md">
        {/* Left Column */}
        <Grid.Col span={4}>
          <Stack spacing="sm">
            {/* Quote Number and Type */}
            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>Quote No.</Text>
              <Text size="sm">:</Text>
              <TextInput
                value={quoteData.quoteNo}
                variant="filled"
                size="sm"
                styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                readOnly
              />
            </Group>
            
            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>Quote Type</Text>
              <Text size="sm">:</Text>
              <Select
                value={quoteData.quoteType}
                data={['Quotation', 'Proposal', 'Estimate']}
                variant="filled"
                size="sm"
                styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                onChange={(value) => setQuoteData(prev => ({ ...prev, quoteType: value }))}
              />
            </Group>

            {/* Customer Section */}
            <Box mt="md">
              <Group spacing="xs" align="center" mb="xs">
                <Box
                  w={16}
                  h={16}
                  style={{
                    backgroundColor: 'red',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Text size="xs" c="white" fw="bold">C</Text>
                </Box>
                <TextInput
                  value={quoteData.customerCode}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  flex={1}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, customerCode: e.target.value }))}
                />
                <TextInput
                  value={quoteData.customerName}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  style={{ flexGrow: 2 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, customerName: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Attn:</Text>
                <TextInput
                  value={quoteData.attn}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, attn: e.target.value }))}
                />
                <TextInput
                  value=""
                  variant="filled"
                  size="sm"
                  style={{ flexGrow: 2 }}
                />
              </Group>
            </Box>

            {/* Shipping Section */}
            <Box mt="md">
              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>F.O.B.:</Text>
                <TextInput
                  value={quoteData.fob}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, fob: e.target.value }))}
                />
                <TextInput
                  value={quoteData.fobLocation}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  style={{ flexGrow: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, fobLocation: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Ship Via:</Text>
                <TextInput
                  value={quoteData.shipVia}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, shipVia: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Terms:</Text>
                <TextInput
                  value={quoteData.terms}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, terms: e.target.value }))}
                />
                <TextInput
                  value={quoteData.termsDescription}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  style={{ flexGrow: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, termsDescription: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Prepared by:</Text>
                <TextInput
                  value={quoteData.preparedBy}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, preparedBy: e.target.value }))}
                />
                <TextInput
                  value={quoteData.preparedByName}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  style={{ flexGrow: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, preparedByName: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Priority:</Text>
                <TextInput
                  value={quoteData.priority}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, priority: e.target.value }))}
                />
              </Group>
            </Box>

            {/* Message and Memo Buttons */}
            <Group mt="md" spacing="sm">
              <Button variant="default" size="sm" leftSection="📝">
                Message
              </Button>
              <Button variant="default" size="sm" leftSection="📄">
                Memo
              </Button>
            </Group>
          </Stack>
        </Grid.Col>

        {/* Middle Column */}
        <Grid.Col span={4}>
          <Stack spacing="sm">
            {/* Reference Fields */}
            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>Our Ref:</Text>
              <TextInput
                value={quoteData.ourRef}
                variant="filled"
                size="sm"
                style={{ flex: 1 }}
                onChange={(e) => setQuoteData(prev => ({ ...prev, ourRef: e.target.value }))}
              />
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>Your Ref:</Text>
              <TextInput
                value={quoteData.yourRef}
                variant="filled"
                size="sm"
                style={{ flex: 1 }}
                onChange={(e) => setQuoteData(prev => ({ ...prev, yourRef: e.target.value }))}
              />
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>B SO Order:</Text>
              <TextInput
                value={quoteData.bsoOrder}
                variant="filled"
                size="sm"
                style={{ flex: 1 }}
                onChange={(e) => setQuoteData(prev => ({ ...prev, bsoOrder: e.target.value }))}
              />
            </Group>

            <Group spacing="xs" align="flex-end">
              <Text size="sm" fw={500}>B Cust PO:</Text>
              <TextInput
                value={quoteData.bCustPO}
                variant="filled"
                size="sm"
                style={{ flex: 1 }}
                onChange={(e) => setQuoteData(prev => ({ ...prev, bCustPO: e.target.value }))}
              />
            </Group>

            {/* Dates Section */}
            <Box mt="md">
              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Entered:</Text>
                <DateTimePicker
                  value={quoteData.entered}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, entered: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Req On:</Text>
                <DateTimePicker
                  value={quoteData.reqOn}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, reqOn: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Due On:</Text>
                <DateTimePicker
                  value={quoteData.dueOn}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, dueOn: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Priced:</Text>
                <DateTimePicker
                  value={quoteData.priced}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, priced: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Quoted:</Text>
                <DateTimePicker
                  value={quoteData.quoted}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, quoted: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Valid For:</Text>
                <NumberInput
                  value={quoteData.validFor}
                  variant="filled"
                  size="sm"
                  w={80}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, validFor: value }))}
                />
                <Text size="sm">days</Text>
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Valid Until:</Text>
                <DateTimePicker
                  value={quoteData.validUntil}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  withSeconds
                  onChange={(value) => setQuoteData(prev => ({ ...prev, validUntil: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end" mb="xs">
                <Text size="sm" fw={500}>Last Print:</Text>
                <TextInput
                  value={quoteData.lastPrint}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, lastPrint: e.target.value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Revision:</Text>
                <TextInput
                  value={quoteData.revision}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, revision: e.target.value }))}
                />
              </Group>
            </Box>
          </Stack>
        </Grid.Col>

        {/* Right Column */}
        <Grid.Col span={4}>
          <Stack spacing="md">
            {/* Doc Status */}
            <Paper withBorder p="sm">
              <Text size="sm" fw={500} ta="center" mb="xs">Doc Status</Text>
              <Badge variant="filled" size="lg" w="100%" style={{ textAlign: 'center' }}>
                {quoteData.docStatus}
              </Badge>
              <Group spacing="xs" align="flex-end" mt="sm">
                <Text size="sm" fw={500}>Hold by/on:</Text>
                <TextInput
                  value={quoteData.holdBy}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, holdBy: e.target.value }))}
                />
              </Group>
            </Paper>

            {/* Totals Section */}
            <Stack spacing="xs">
              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Sub Total:</Text>
                <NumberInput
                  value={quoteData.subTotal}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, subTotal: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Discount:</Text>
                <NumberInput
                  value={quoteData.discount}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  styles={{ input: { textAlign: 'right' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, discount: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Misc Ttl:</Text>
                <NumberInput
                  value={quoteData.miscTtl}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  styles={{ input: { textAlign: 'right' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, miscTtl: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Tax Ttl:</Text>
                <NumberInput
                  value={quoteData.taxTtl}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  styles={{ input: { textAlign: 'right' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, taxTtl: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Doc Total:</Text>
                <NumberInput
                  value={quoteData.docTotal}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, docTotal: value }))}
                />
              </Group>
            </Stack>

            {/* Currency Section */}
            <Stack spacing="xs" mt="md">
              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}> Cmcy Base:</Text>
                <Select
                  value={quoteData.cmcyBase}
                  data={['USD', 'EUR', 'CAD', 'GBP']}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, cmcyBase: value }))}
                />
              </Group>

              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>Cmcy Conv:</Text>
                <Select
                  value={quoteData.cmcyConv}
                  data={['USD', 'EUR', 'CAD', 'GBP']}
                  variant="filled"
                  size="sm"
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(value) => setQuoteData(prev => ({ ...prev, cmcyConv: value }))}
                />
              </Group>
            </Stack>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default HeaderTab;